import { observabilityDb } from '../storage/observability-db';
import { sweepExpiredEvents, trimToByteBudget } from '../storage/eviction';
import type { SwDownloadFailurePayload } from '../observability.events';

declare const __TEMPLATE__: string;
declare const __SW_VERSION__: string;
// Not `@config` (`virtual:pwa-config`) -- the service worker is built by
// vite-plugin-pwa's injectManifest in a reduced Vite environment that
// doesn't include this repo's plugins, so that virtual module fails to
// resolve there. These three exist specifically for this file --
// see create-app-config.ts's own comment on them.
declare const __APP_KEY__: string;
declare const __BUILD_VERSION__: string;
declare const __BUILD_NUMBER__: number;

/**
 * The service worker's own path to the observability store -- deliberately
 * NOT `report()`/`local-reporter.ts`. That reporter's memory-first
 * dedupe/flush machinery is built around `document`/`window` (visibility
 * change, pagehide), neither of which exists in a service worker's global
 * scope. A SW instance is also short-lived and event-driven, so buffering
 * in memory and hoping a later flush fires is the wrong shape here anyway
 * -- writing directly, immediately, is what "no dependency on an open
 * page" (improvements/16-observability-error-handling.md) actually means.
 *
 * No in-memory dedupe either, for the same reason: there's no reliable
 * "later" to coalesce into. Each call is one row. Download failures are
 * infrequent enough (not a render loop) that this doesn't reproduce the
 * write-storm problem the page-side dedupe exists to prevent.
 */
export async function reportSwDownloadFailure(
	payload: SwDownloadFailurePayload,
): Promise<void> {
	const row = {
		name: 'sw_download_failure' as const,
		appKey: __APP_KEY__,
		template: __TEMPLATE__,
		version: __BUILD_VERSION__,
		buildNumber: __BUILD_NUMBER__,
		swVersion:
			typeof __SW_VERSION__ !== 'undefined' ? __SW_VERSION__ : 'dev',
		timestamp: Date.now(),
		dedupeKey: `sw_download_failure:${payload.cacheName}`,
		occurrences: 1,
		payload: { ...payload },
	};

	try {
		await observabilityDb.events.add({
			...row,
			approxBytes: JSON.stringify(row).length,
		});
		// Cheap and marker-gated (see eviction.ts) -- safe to call on every
		// write rather than needing its own SW-side scheduling.
		await sweepExpiredEvents();
		await trimToByteBudget();
	} catch {
		// Best-effort: a failed write here must not break the download
		// pipeline it's reporting on, and there is no fallback tier to hand
		// this to -- the SW has no window/document to buffer against, and
		// the page may not even be open.
	}
}
